import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, ArrowLeft, BookOpen, Globe, Mail, Phone, AlertTriangle, Copyright } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST_UPDATE = "10 de abril de 2026";

function LegalHeader({ company }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/superpoderes" className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft size={18} className="text-gray-600" /></a>
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center"><BookOpen size={15} className="text-white" /></div>
          <span className="font-semibold text-gray-900">{company?.app_name || "OkBot"}</span>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="/terminos" className="text-gray-500 hover:text-gray-900 transition-colors">Términos</a>
          <a href="/privacidad" className="text-gray-500 hover:text-gray-900 transition-colors">Privacidad</a>
          <a href="/aviso-legal" className="text-red-600 font-medium border-b-2 border-red-600 pb-0.5">Aviso Legal</a>
          <a href="/cookies" className="text-gray-500 hover:text-gray-900 transition-colors">Cookies</a>
        </nav>
      </div>
    </header>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100">{title}</h2>
      <div className="text-gray-600 leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function LegalNoticePage() {
  const [company, setCompany] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/legal/public-info`).then(r => setCompany(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const c = company || {};
  const companyName = c.company_name || "OkBot";
  const email = c.email || "hola@okgo.mx";
  const website = c.website || "https://okbot.okgo.mx";
  const appName = c.app_name || "OkBot";

  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader company={company} />

      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
            <a href="/superpoderes" className="hover:text-white">Inicio</a>
            <ChevronRight size={14} />
            <span>Aviso Legal</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <BookOpen size={36} className="text-gray-400" />
            <div>
              <h1 className="text-4xl font-bold">Aviso Legal</h1>
              <p className="text-gray-400 mt-1">Información legal sobre el titular y condiciones de uso del sitio web</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 mt-8 pt-8 border-t border-gray-700 text-sm text-gray-400">
            <div><span className="text-gray-500 block text-xs uppercase tracking-wide mb-1">Última actualización</span>{LAST_UPDATE}</div>
            <div><span className="text-gray-500 block text-xs uppercase tracking-wide mb-1">Titular</span>{companyName}</div>
            <div><span className="text-gray-500 block text-xs uppercase tracking-wide mb-1">País</span>México</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {[
            { icon: <Globe size={20} className="text-red-600" />, title: "Sitio Web", value: website, href: website },
            { icon: <Mail size={20} className="text-red-600" />, title: "Contacto", value: email, href: `mailto:${email}` },
            { icon: <Copyright size={20} className="text-red-600" />, title: "Titular", value: companyName, href: null },
          ].map(({ icon, title, value, href }) => (
            <div key={title} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-gray-500 uppercase tracking-wide">{title}</span></div>
              {href
                ? <a href={href} className="text-gray-900 font-semibold hover:text-red-600 transition-colors text-sm">{value}</a>
                : <p className="text-gray-900 font-semibold text-sm">{value}</p>}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">

          <Block title="1. Identificación del Titular">
            <p>En cumplimiento con la legislación mexicana aplicable, se informa que el sitio web <a href={website} className="text-red-600 hover:underline">{website}</a> es operado por <strong>{companyName}</strong>, en adelante "el Titular".</p>
            <table className="w-full mt-4 border border-gray-200 rounded-lg overflow-hidden text-sm">
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Denominación / Nombre", companyName],
                  ...(c.rfc ? [["RFC", c.rfc]] : []),
                  ...(c.legal_rep ? [["Representante Legal", c.legal_rep]] : []),
                  ...(c.address ? [["Domicilio", [c.address, c.city, c.state, c.zip_code, "México"].filter(Boolean).join(", ")]] : []),
                  ["Correo electrónico", email],
                  ["Sitio web", website],
                ].map(([k, v]) => (
                  <tr key={k}><td className="py-3 px-4 font-medium text-gray-700 w-48">{k}</td><td className="py-3 px-4 text-gray-600">{v}</td></tr>
                ))}
              </tbody>
            </table>
          </Block>

          <Block title="2. Objeto y Condiciones de Uso">
            <p>El presente Aviso Legal regula el acceso y uso del sitio web {website} y de la plataforma {appName}. El simple acceso al sitio web implica la aceptación plena y sin reservas de las condiciones contenidas en el presente aviso legal.</p>
            <p>El Titular se reserva el derecho de modificar el sitio web, sus servicios, contenidos y condiciones de uso en cualquier momento y sin previo aviso. El usuario se compromete a hacer un uso adecuado de los contenidos y servicios, conforme a la legislación vigente en México, el presente aviso legal y las buenas costumbres.</p>
          </Block>

          <Block title="3. Propiedad Intelectual e Industrial">
            <p>Todos los contenidos del sitio web y de la Plataforma, incluyendo sin limitación el diseño gráfico, código fuente, textos, imágenes, logotipos, marcas, nombres comerciales, íconos y cualquier otro elemento, son propiedad del Titular o de sus licenciantes, y están protegidos por:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
              <li>Ley Federal del Derecho de Autor (LFDA)</li>
              <li>Ley de la Propiedad Industrial (LPI)</li>
              <li>Código Civil Federal en materia de responsabilidad civil</li>
              <li>Tratados internacionales suscritos por México</li>
            </ul>
            <p className="mt-3">Queda expresamente prohibida la reproducción, distribución, transformación o comunicación pública, total o parcial, de los contenidos de este sitio web sin autorización expresa y por escrito del Titular.</p>
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
              <div className="flex items-start gap-2">
                <Copyright size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-600">© {new Date().getFullYear()} {companyName}. Todos los derechos reservados. La marca "{appName}" y sus logotipos son marcas protegidas.</p>
              </div>
            </div>
          </Block>

          <Block title="4. Exclusión de Responsabilidad">
            <p>El Titular no se hace responsable por:</p>
            <div className="space-y-3 mt-3">
              {[
                ["Calidad y continuidad del servicio", "El Titular no garantiza la ausencia de interrupciones o errores en el acceso al sitio web o a la Plataforma, aunque realizará sus mejores esfuerzos para mantener la disponibilidad del servicio."],
                ["Contenido de terceros", "El sitio puede contener enlaces a sitios web de terceros. El Titular no controla dichos sitios ni se hace responsable de su contenido, política de privacidad o disponibilidad."],
                ["Virus y seguridad informática", "El Titular adopta medidas de seguridad razonables, pero no garantiza la ausencia de virus u otros elementos dañinos en los contenidos que puedan causar daños en los sistemas informáticos del usuario."],
                ["Uso indebido de la plataforma", "El Titular no será responsable de los daños producidos por el uso inapropiado de la Plataforma por parte de los usuarios."],
              ].map(([titulo, desc]) => (
                <div key={titulo} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div><p className="font-medium text-gray-900 text-sm mb-1">{titulo}</p><p className="text-sm text-gray-600">{desc}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </Block>

          <Block title="5. Política de Privacidad y Cookies">
            <p>El tratamiento de los datos personales de los usuarios se rige por nuestra <a href="/privacidad" className="text-red-600 hover:underline font-medium">Política de Privacidad</a>, elaborada conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento.</p>
            <p className="mt-3">El sitio web utiliza cookies propias y de terceros. Para más información consulta nuestra <a href="/cookies" className="text-red-600 hover:underline font-medium">Política de Cookies</a>.</p>
          </Block>

          <Block title="6. Legislación Aplicable y Jurisdicción">
            <p>El presente Aviso Legal se rige e interpreta conforme a las leyes vigentes en los <strong>Estados Unidos Mexicanos</strong>. Para la resolución de cualquier controversia derivada del acceso o uso de este sitio web, las partes se someten expresamente a la jurisdicción de los <strong>Tribunales competentes de la Ciudad de México</strong>, renunciando a cualquier otro fuero que pudiera corresponderles.</p>
          </Block>

          <Block title="7. Modificaciones del Aviso Legal">
            <p>El Titular se reserva el derecho de revisar y actualizar el presente Aviso Legal en cualquier momento. Las modificaciones entrarán en vigor desde el momento de su publicación en el sitio web. El uso continuado del sitio web después de dichas modificaciones implicará la aceptación de las mismas.</p>
          </Block>

          <Block title="8. Contacto">
            <p>Para cualquier consulta relacionada con el presente Aviso Legal, puede contactarnos:</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <a href={`mailto:${email}`} className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-red-200 hover:bg-red-50 transition-colors flex-1">
                <Mail size={18} className="text-red-600" />
                <div><p className="text-xs text-gray-500 uppercase tracking-wide">Email</p><p className="text-gray-900 font-medium text-sm">{email}</p></div>
              </a>
              <a href={website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-red-200 hover:bg-red-50 transition-colors flex-1">
                <Globe size={18} className="text-red-600" />
                <div><p className="text-xs text-gray-500 uppercase tracking-wide">Sitio Web</p><p className="text-gray-900 font-medium text-sm">{website}</p></div>
              </a>
            </div>
          </Block>

        </div>
      </div>

      <footer className="bg-gray-900 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm">© {new Date().getFullYear()} {companyName}. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm">
            <a href="/terminos" className="hover:text-gray-200 transition-colors">Términos</a>
            <a href="/privacidad" className="hover:text-gray-200 transition-colors">Privacidad</a>
            <a href="/aviso-legal" className="text-red-400 hover:text-red-300 transition-colors">Aviso Legal</a>
            <a href="/cookies" className="hover:text-gray-200 transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
